// core
import React from "react";

interface IUseAccordion<T> {
    sectionIsOpen: (panel: T) => boolean;
    sectionIsAnimating: (panel: T) => boolean;
    handleChangeSection: (panel: T, isExpanded: boolean, isAnimated?: boolean) => void;
    setAnimationComplete: (panel: T) => void;
    hasAnimatingPanel: boolean;
}

function useAccordion<T extends string>(
    defaultOpenPanel?: T,
    singlePanel = true
): IUseAccordion<T> {
    type PanelController = {
        [key in T]?: boolean;
    };

    const defaultPanel: PanelController = {};

    if (defaultOpenPanel) {
        defaultPanel[defaultOpenPanel] = true;
    }

    const [isExpanded, setIsExpanded] = React.useState<PanelController>(defaultPanel);
    const [isAnimating, setIsAnimating] = React.useState<PanelController>(defaultPanel);

    const handleChangeSection = (
        panel: T,
        panelIsExpanded: boolean,
        isAnimated = true
    ): void => {
        if (!panel) return;

        isAnimated &&
            setIsAnimating({
                ...isAnimating,
                [panel]: true
            });

        if (singlePanel) {
            const defaultPanelChange: PanelController = {};
            defaultPanelChange[panel] = panelIsExpanded;
            setIsExpanded(defaultPanelChange);
        } else {
            setIsExpanded({
                ...isExpanded,
                [panel]: panelIsExpanded
            });
        }
    };

    const sectionIsOpen = (panel: T): boolean => !!isExpanded[panel];

    const sectionIsAnimating = (panel: T): boolean => !!isAnimating[panel];

    const setAnimationComplete = (panel: T): void =>
        panel &&
        setIsAnimating({
            ...isAnimating,
            [panel]: false
        });

    const hasAnimatingPanel = (): boolean => {
        return Object.keys(isAnimating).some((k) => isAnimating[k]);
    };

    return {
        sectionIsOpen,
        sectionIsAnimating,
        handleChangeSection,
        setAnimationComplete,
        hasAnimatingPanel: hasAnimatingPanel()
    };
}

export default useAccordion;
